import React, { useEffect, useState, useContext } from 'react';
import apiClient from '../../apiClient';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { toast } from 'react-toastify';
import './KallelseTemplateList.css';

function KallelseTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showDropdownId, setShowDropdownId] = useState(null);

  /* ------- Hämta clinics + mallar ---------- */
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Fel vid hämtning av clinics:', err));

    apiClient.get('/api/kallelsemallar')
      .then(res => setTemplates(res.data))
      .catch(err => {
        console.error('Fel vid hämtning av mallar:', err);
        setError('Misslyckades att hämta mallar.');
      });
  }, []);

  /* ------- CRUD-handlers ---------- */
  const handleEdit = (id) => navigate(`/kallelsemallar/edit/${id}`);
  const handleCreate = () => navigate('/kallelsemallar/create');

  const handleDelete = (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna kallelsemall?')) return;

    apiClient.delete(`/api/kallelsemallar/${id}`)
      .then(() => {
        setTemplates(templates.filter(t => t.template_id !== id));
        toast.success('Kallelsemall borttagen');
      })
      .catch(err => {
        console.error('Fel vid borttagning av mall:', err);
        toast.error(err.response?.data?.msg || 'Misslyckades att ta bort mall');
      });
  };

  const copyTemplate = async (templateId, clinicId) => {
    try {
      await apiClient.post(`/api/kallelsemallar/${templateId}/copy`, {
        clinic_id: clinicId
      });
      toast.success('Mall kopierad!');
      // Uppdatera listan
      apiClient.get('/api/kallelsemallar')
        .then(res => setTemplates(res.data))
        .catch(err => console.error('Fel vid uppdatering av mallar:', err));
    } catch (err) {
      console.error('Kopiering misslyckades:', err);
      toast.error(err.response?.data?.msg || 'Kopiering misslyckades');
    }
  };

  /* ------- Filtrering ---------- */
  const filteredTemplates = selectedClinicId
    ? templates.filter(t => t.clinic_id === parseInt(selectedClinicId))
    : templates;

  /* ------- Admin-check (global admin + klinikadmin) ---------- */
  const isAdmin = isClinicLevelAdmin(user);

  return (
    <div className="kallelse-template-list-container">
      <div className="kallelse-template-list-header">
        <h2>Kallelsemallar</h2>
        <div className="header-controls">
          {isAdmin && (
            <select
              value={selectedClinicId}
              onChange={(e) => setSelectedClinicId(e.target.value)}
              className="clinic-filter-select"
            >
              <option value="">Alla kliniker</option>
              {clinics.map(c => (
                <option key={c.clinic_id} value={c.clinic_id}>
                  {c.clinic_name}
                </option>
              ))}
            </select>
          )}
          {isAdmin && (
            <button onClick={handleCreate} className="create-button">
              + Skapa ny mall
            </button>
          )}
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="template-list">
        {filteredTemplates.length === 0 ? (
          <p className="no-templates">Inga kallelsemallar hittades.</p>
        ) : (
          filteredTemplates.map(template => (
            <div key={template.template_id} className="template-card">
              <div className="template-info">
                <h3 className="template-name">{template.template_name}</h3>
                <p className="template-subject">
                  <strong>Ämne:</strong> {template.subject}
                </p>
                <p className="template-clinic">
                  {template.clinic_name || 'Global mall'}
                </p>
                <p className="template-preview">
                  {template.body?.substring(0, 100)}...
                </p>
              </div>

              <div className="template-actions">
                {isAdmin && (
                  <>
                    <button
                      className="edit-button"
                      onClick={() => handleEdit(template.template_id)}
                    >
                      Redigera
                    </button>

                    <div className="copy-dropdown-container">
                      <button
                        className="copy-button"
                        onClick={() => setShowDropdownId(
                          showDropdownId === template.template_id ? null : template.template_id
                        )}
                      >
                        Kopiera till...
                      </button>

                      {showDropdownId === template.template_id && (
                        <div className="copy-dropdown">
                          {clinics
                            .filter(c => c.clinic_id !== template.clinic_id)
                            .map(clinic => (
                              <button
                                key={clinic.clinic_id}
                                onClick={() => {
                                  copyTemplate(template.template_id, clinic.clinic_id);
                                  setShowDropdownId(null);
                                }}
                              >
                                {clinic.clinic_name}
                              </button>
                            ))
                          }
                        </div>
                      )}
                    </div>

                    <button
                      className="delete-button"
                      onClick={() => handleDelete(template.template_id)}
                    >
                      Ta bort
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default KallelseTemplateList;
